'use client'

import { createContext, ReactNode, useContext, useEffect, useState } from 'react'

import { resolveTenantSubdomainFromHostname } from '@/lib/resolve-tenant-subdomain'

import { baseService } from '@/services/base.service'
import communityMediaDefaultService from '@/services/community-media-default.service'
import tenantService, { TenantConfig } from '@/services/tenant.service'
import { getTenantFromWindow, isOnFullstackdatascienceDomain } from '@/utils/tenant.utils'
import {
  clearTenantConfigStorage,
  getTenantConfigFromStorage,
  setTenantConfigStorage,
} from '@/utils/tenant-cache.utils'

interface TenantContextType {
  tenantConfig: TenantConfig | null
  tenantId: string
  isLoading: boolean
  error: Error | null
  refreshConfig: () => Promise<void>
}

const TenantContext = createContext<TenantContextType | undefined>(undefined)

interface TenantProviderProps {
  children: ReactNode
  initialTenantId?: string
  initialConfig?: TenantConfig | null
}

export function TenantProvider({ children, initialTenantId, initialConfig }: TenantProviderProps) {
  const initialResolvedTenantId = initialTenantId || getTenantFromWindow()
  const shouldRefreshInitialConfig = Boolean(
    initialConfig &&
      initialResolvedTenantId &&
      initialResolvedTenantId !== 'default' &&
      !Object.prototype.hasOwnProperty.call(initialConfig, 'customization')
  )
  const shouldFetchInitialConfig = Boolean(
    initialResolvedTenantId &&
      initialResolvedTenantId !== 'default' &&
      (!initialConfig || shouldRefreshInitialConfig)
  )
  const [tenantId, setTenantId] = useState<string>(initialResolvedTenantId)
  const [tenantConfig, setTenantConfig] = useState<TenantConfig | null>(initialConfig || null)
  const [isLoading, setIsLoading] = useState<boolean>(shouldFetchInitialConfig)
  const [error, setError] = useState<Error | null>(null)

  // Set initial config in BaseService to prevent it from fetching
  useEffect(() => {
    if (initialConfig && initialTenantId) {
      baseService.setTenantConfig(initialTenantId, initialConfig)
    }
  }, [initialConfig, initialTenantId])

  const fetchTenantConfig = async (id: string, skipCache = false) => {
    try {
      setIsLoading(true)
      setError(null)

      const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
      const isFsdsDomain = isOnFullstackdatascienceDomain()

      // Check localStorage cache first (client-side only)
      if (!skipCache && typeof window !== 'undefined') {
        const cachedConfig = getTenantConfigFromStorage(id)
        if (cachedConfig) {
          let config = cachedConfig
          if (!isFsdsDomain) {
            try {
              const mediaDefaults = await communityMediaDefaultService.getBySubdomain(id)
              config = {
                ...cachedConfig,
                theme: communityMediaDefaultService.mergeThemeWithMediaDefaults(
                  cachedConfig.theme,
                  mediaDefaults
                ),
                customization: communityMediaDefaultService.mergeIntoCustomization(
                  cachedConfig.customization,
                  mediaDefaults
                ),
              }
            } catch {
              // Use cached config without refreshing media
            }
          }
          setTenantConfig(config)
          setTenantId(id)
          baseService.setTenantConfig(id, config)
          setIsLoading(false)
          return
        }
      }

      // Community hosts: eztech-be only. FSDS hosts: fsds-be (includes media defaults).
      const response = await tenantService.getTenantBySubdomain(id, hostname)

      if (response) {
        setTenantConfig(response)
        setTenantId(id)

        // Cache in localStorage
        if (typeof window !== 'undefined') {
          setTenantConfigStorage(id, response)
        }

        // Set in BaseService to prevent it from fetching on API calls
        baseService.setTenantConfig(id, response)
      } else if (isFsdsDomain) {
        const fallbackConfig: TenantConfig = {
          id,
          name: '',
          subdomain: id,
          theme: {},
          features: [],
          status: 'ACTIVE',
        }

        setTenantConfig(fallbackConfig)
        setTenantId(id)

        if (typeof window !== 'undefined') {
          setTenantConfigStorage(id, fallbackConfig)
        }

        baseService.setTenantConfig(id, fallbackConfig)
      } else {
        // Community host with no registered community — do not invent a tenant.
        setTenantConfig(null)
        setTenantId(id)
        setError(new Error(`Community not found for subdomain "${id}"`))
        clearTenantConfigStorage(id)
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to get tenant config')
      setError(error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!tenantId || tenantId === 'default') {
      setIsLoading(false)
      return
    }

    // _app may seed a synthetic config from the host. Refresh it so tenant customization is available.
    if (shouldRefreshInitialConfig) {
      fetchTenantConfig(tenantId, true)
      return
    }

    // Only fetch if we don't have initial config
    if (!initialConfig) {
      fetchTenantConfig(tenantId)
    }
  }, [tenantId, initialConfig, shouldRefreshInitialConfig])

  // Resolve custom domains (e.g. robusto.ai) and refresh on client navigation
  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    let cancelled = false

    ;(async () => {
      const resolved = await resolveTenantSubdomainFromHostname(window.location.hostname)
      if (cancelled || resolved === tenantId) {
        return
      }
      setTenantId(resolved)
      if (resolved !== 'default') {
        await fetchTenantConfig(resolved)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [tenantId])

  const refreshConfig = async () => {
    if (tenantId && tenantId !== 'default') {
      // Skip cache when explicitly refreshing
      await fetchTenantConfig(tenantId, true)
    }
  }

  const value: TenantContextType = {
    tenantConfig,
    tenantId,
    isLoading,
    error,
    refreshConfig,
  }

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
}

export function useTenant(): TenantContextType {
  const context = useContext(TenantContext)
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider')
  }
  return context
}
